import React from 'react';

export type Contract = {
  plan: string;
  fee: string;
  appliedAt: string;
  startedAt: string;
  renewedAt: string;
  paymentMethod: string;
  usageCount: string;
  annualUsageCount: string;
  status: string;
  agencyNo: string;
  agencyName: string;
};

export type ContractSectionProps = {
  contracts: Contract[];
  setContracts: React.Dispatch<React.SetStateAction<Contract[]>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  CONTRACT_PLANS: { value: string; label: string }[];
  CONTRACT_PAYMENT_METHODS: { value: string; label: string }[];
  CONTRACT_STATUSES: { value: string; label: string }[];
};

const ContractSection: React.FC<ContractSectionProps> = ({
  contracts,
  setContracts,
  open,
  setOpen,
  CONTRACT_PLANS,
  CONTRACT_PAYMENT_METHODS,
  CONTRACT_STATUSES,
}) => {
  const handleContractChange = (idx: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setContracts((prev) => prev.map((c, i) =>
      i === idx ? { ...c, [name]: type === 'number' ? Number(value) : value } : c
    ));
  };
  const handleContractStatusChange = (idx: number, value: string) => {
    setContracts((prev) => prev.map((c, i) =>
      i === idx ? { ...c, status: value } : c
    ));
  };
  const addContract = () => {
    setContracts((prev) => [
      ...prev,
      {
        plan: '',
        fee: '',
        appliedAt: '',
        startedAt: '',
        renewedAt: '',
        paymentMethod: '',
        usageCount: '',
        annualUsageCount: '',
        status: 'active',
        agencyNo: '',
        agencyName: '',
      },
    ]);
  };
  const removeContract = (idx: number) => {
    setContracts((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <button type="button" className={`flex items-center justify-between w-full pt-1 pb-2 text-left text-lg font-bold text-gray-800 border-b border-gray-200 focus:outline-none`} onClick={() => setOpen((v) => !v)}>
        <span>契約情報</span>
        <span className="text-base">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="p-4 text-sm text-gray-600 bg-white border border-gray-200 rounded-b-lg shadow-md">
          <div className="flex flex-col gap-6">
            {contracts.map((c, idx) => (
              <div key={idx} className="relative p-4 border border-gray-100 rounded-lg bg-gray-50">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">契約プラン</label>
                    <select name="plan" className="w-full px-2 py-1 text-sm font-semibold text-gray-500 border border-gray-200 rounded" value={c.plan} onChange={e => handleContractChange(idx, e)}>
                      <option value="">選択してください</option>
                      {CONTRACT_PLANS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">契約料金</label>
                    <input type="number" name="fee" className="w-full px-2 py-1 border border-gray-200 rounded" value={c.fee} onChange={e => handleContractChange(idx, e)} />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">申込日</label>
                    <input type="date" name="appliedAt" className="w-full px-2 py-1 border border-gray-200 rounded" value={c.appliedAt} onChange={e => handleContractChange(idx, e)} />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">開始日</label>
                    <input type="date" name="startedAt" className="w-full px-2 py-1 border border-gray-200 rounded" value={c.startedAt} onChange={e => handleContractChange(idx, e)} />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">契約更新日</label>
                    <input type="date" name="renewedAt" className="w-full px-2 py-1 border border-gray-200 rounded" value={c.renewedAt} onChange={e => handleContractChange(idx, e)} />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">お支払い方法</label>
                    <select name="paymentMethod" className="w-full px-2 py-1 text-sm font-semibold text-gray-500 border border-gray-200 rounded" value={c.paymentMethod} onChange={e => handleContractChange(idx, e)}>
                      <option value="">選択してください</option>
                      {CONTRACT_PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">開始日からの利用回数</label>
                    <input type="number" name="usageCount" className="w-full px-2 py-1 border border-gray-200 rounded" value={c.usageCount} onChange={e => handleContractChange(idx, e)} />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">年間利用回数</label>
                    <input type="number" name="annualUsageCount" className="w-full px-2 py-1 border border-gray-200 rounded" value={c.annualUsageCount} onChange={e => handleContractChange(idx, e)} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block mb-1 text-sm font-semibold text-gray-500">ステータス</label>
                    <div className="flex gap-4 mt-1">
                      {CONTRACT_STATUSES.map(s => (
                        <label key={s.value} className="flex items-center gap-1">
                          <input type="radio" name={`status-${idx}`} checked={c.status === s.value} onChange={() => handleContractStatusChange(idx, s.value)} />
                          <span>{s.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">代理店No</label>
                    <input type="text" name="agencyNo" className="w-full px-2 py-1 border border-gray-200 rounded" value={c.agencyNo} onChange={e => handleContractChange(idx, e)} />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-500">代理店名</label>
                    <input type="text" name="agencyName" className="w-full px-2 py-1 border border-gray-200 rounded" value={c.agencyName} onChange={e => handleContractChange(idx, e)} />
                  </div>
                </div>
                <button type="button" className="absolute text-xs text-red-500 underline top-2 right-2" onClick={() => removeContract(idx)} disabled={contracts.length === 1}>削除</button>
              </div>
            ))}
            <button type="button" className="px-4 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded hover:bg-blue-200" onClick={addContract}>＋ 契約情報を追加</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractSection;
